import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from './constants';

/**
 * Descarga un despacho en formato Excel
 */
export const downloadDispatchExcel = (dispatch) => {
  const trucksList = dispatch.trucks || [];
  
  const data = trucksList.map((truck) => {
    const kgDiff = (truck.kg_loaded || 0) - (truck.kg_unloaded || 0);
    const tonDiff = kgDiff / 1000;
    const concatenado = `${truck.broker || ''}/${truck.destination || ''}/${truck.sale_invoice || ''}`;
    
    return {
      'F. CARGA': truck.load_date || '',
      'F. DESCARGA': truck.delivery_date || '',
      '#': truck.order,
      'DESPACHO': dispatch.dispatch_number,
      'TRANSPORTE': truck.company_name || '',
      'FACT. TRANSP.': truck.transport_invoice || '',
      'PATENTE': truck.license_plate || '',
      'CHOFER': truck.driver_name || '',
      'TON. CARG.': truck.kg_loaded ? parseFloat((truck.kg_loaded / 1000).toFixed(3)) : '',
      'CRT': dispatch.crt_number,
      'P. FLETE': truck.freight_value || '',
      'CORREDOR/CLIENTE/FACT. VENTA': concatenado,
      'COMUNA': truck.commune || '',
      'PRECIO': truck.sale_price || '',
      'GUIA': truck.guide_number || '',
      'TON. DESC.': truck.kg_unloaded ? parseFloat((truck.kg_unloaded / 1000).toFixed(3)) : '',
      'DIF.(T)': truck.kg_loaded && truck.kg_unloaded ? parseFloat(tonDiff.toFixed(3)) : ''
    };
  });
  
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Despacho');
  
  ws['!cols'] = [
    { wch: 12 }, { wch: 12 }, { wch: 4 }, { wch: 12 }, { wch: 20 },
    { wch: 12 }, { wch: 10 }, { wch: 18 }, { wch: 12 }, { wch: 15 },
    { wch: 10 }, { wch: 35 }, { wch: 12 }, { wch: 10 }, { wch: 12 },
    { wch: 12 }, { wch: 10 }
  ];
  
  XLSX.writeFile(wb, `Despacho_${dispatch.dispatch_number}.xlsx`);
  toast.success(`Despacho ${dispatch.dispatch_number} descargado`);
};

/**
 * Descarga múltiples despachos en un solo archivo Excel
 * Cada despacho aparece en un bloque separado por una línea en blanco
 */
export const downloadMultipleDispatchesExcel = (dispatches) => {
  if (!dispatches || dispatches.length === 0) {
    toast.error('No hay despachos seleccionados');
    return;
  }

  toast.info(`Generando Excel con ${dispatches.length} despacho(s)...`);
  
  // Array para acumular todas las filas
  const allRows = [];
  
  // Encabezados de columnas
  const headers = [
    'F. CARGA', 'F. DESCARGA', '#', 'DESPACHO', 'TRANSPORTE', 
    'FACT. TRANSP.', 'PATENTE', 'CHOFER', 'TON. CARG.', 'CRT', 
    'P. FLETE', 'CORREDOR/CLIENTE/FACT. VENTA', 'COMUNA', 'PRECIO', 
    'GUIA', 'TON. DESC.', 'DIF.(T)'
  ];

  dispatches.forEach((dispatch, index) => {
    const trucksList = dispatch.trucks || [];
    
    // Si no es el primer despacho, agregar línea en blanco
    if (index > 0) {
      allRows.push({}); // Línea en blanco separadora
    }
    
    // Agregar datos de cada camión del despacho
    trucksList.forEach((truck) => {
      const kgDiff = (truck.kg_loaded || 0) - (truck.kg_unloaded || 0);
      const concatenado = `${truck.broker || ''}/${truck.destination || ''}/${truck.sale_invoice || ''}`;
      
      allRows.push({
        'F. CARGA': truck.load_date || '',
        'F. DESCARGA': truck.delivery_date || '',
        '#': truck.order,
        'DESPACHO': dispatch.dispatch_number,
        'TRANSPORTE': truck.company_name || '',
        'FACT. TRANSP.': truck.transport_invoice || '',
        'PATENTE': truck.license_plate || '',
        'CHOFER': truck.driver_name || '',
        'TON. CARG.': truck.kg_loaded ? parseFloat((truck.kg_loaded / 1000).toFixed(3)) : '',
        'CRT': dispatch.crt_number,
        'P. FLETE': truck.freight_value || '',
        'CORREDOR/CLIENTE/FACT. VENTA': concatenado,
        'COMUNA': truck.commune || '',
        'PRECIO': truck.sale_price || '',
        'GUIA': truck.guide_number || '',
        'TON. DESC.': truck.kg_unloaded ? parseFloat((truck.kg_unloaded / 1000).toFixed(3)) : '',
        'DIF.(T)': truck.kg_loaded && truck.kg_unloaded ? parseFloat(((truck.kg_loaded - truck.kg_unloaded) / 1000).toFixed(3)) : ''
      });
    });
    
    // Si el despacho no tiene camiones, agregar fila con info del despacho
    if (trucksList.length === 0) {
      allRows.push({
        'DESPACHO': dispatch.dispatch_number,
        'CRT': dispatch.crt_number,
        'F. CARGA': dispatch.load_date || '',
        'F. DESCARGA': dispatch.delivery_date || '',
        'P. FLETE': dispatch.freight_value_per_ton || ''
      });
    }
  });

  // Crear hoja de cálculo
  const ws = XLSX.utils.json_to_sheet(allRows, { header: headers });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Despachos');
  
  // Configurar anchos de columna
  ws['!cols'] = [
    { wch: 12 }, { wch: 12 }, { wch: 4 }, { wch: 12 }, { wch: 20 },
    { wch: 12 }, { wch: 10 }, { wch: 18 }, { wch: 12 }, { wch: 15 },
    { wch: 10 }, { wch: 35 }, { wch: 12 }, { wch: 10 }, { wch: 12 },
    { wch: 12 }, { wch: 10 }
  ];
  
  // Generar nombre del archivo
  const today = new Date().toISOString().split('T')[0];
  const fileName = dispatches.length === 1 
    ? `Despacho_${dispatches[0].dispatch_number}.xlsx`
    : `Despachos_${dispatches.length}_${today}.xlsx`;
  
  XLSX.writeFile(wb, fileName);
  toast.success(`${dispatches.length} despacho(s) exportados exitosamente`);
};

/**
 * Descarga la nómina de camiones en formato Excel
 */
export const downloadNominaExcel = async (dispatch) => {
  try {
    toast.info('Generando nómina...');
    const response = await axios.get(`${API_URL}/dispatches/${dispatch.id}/nomina`);
    const { nomina } = response.data;
    
    const data = nomina.map((row) => ({
      'TRANSPORTE': row.transporte,
      'DOMICILIO': row.domicilio,
      'CUIT': row.cuit,
      'N° PERMISO INT.': row.permiso_int,
      'VENC. PERMISO INT.': row.venc_permiso_int,
      'N° PAUT': row.paut,
      'PATENTE CAMION': row.patente_camion,
      'MARCA': row.marca_camion,
      'MODELO': row.modelo_camion,
      'AÑO': row.año_camion,
      'CHASIS': row.chasis,
      'N° POLIZA': row.poliza_camion,
      'VENC. POLIZA': row.venc_poliza_camion,
      'PATENTE ACOPLADO': row.patente_acoplado,
      'MARCA ACOPLADO': row.marca_acoplado,
      'N° POLIZA ACOPLADO': row.poliza_acoplado,
      'VENC. POLIZA ACOPLADO': row.venc_poliza_acoplado,
      '# PRECINTO': row.precintos,
      'CHOFER': row.chofer,
      'DNI': row.dni,
      'CUIL': row.cuil,
      'TELEFONO': row.telefono
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Nómina');
    
    ws['!cols'] = [
      { wch: 20 }, { wch: 35 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
      { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 8 },
      { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 },
      { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 20 }, { wch: 12 },
      { wch: 15 }, { wch: 15 }
    ];
    
    XLSX.writeFile(wb, `Nomina_${dispatch.dispatch_number}.xlsx`);
    toast.success(`Nómina del despacho ${dispatch.dispatch_number} descargada`);
  } catch (error) {
    toast.error('Error al generar la nómina');
  }
};
